/**
 * Service CRUD pour la gestion des données
 * Service temporaire pour les opérations CRUD génériques
 */

import { v4 as uuidv4 } from 'uuid';

// Types de base
export interface Client {
  id: string;
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  adresse: string;
  siret?: string;
  dateCreation: string;
  dateModification: string;
}

export interface Note {
  id: string;
  contenu: string;
  dateCreation: string;
  dateModification: string;
  enqueteId: string;
  auteur: string;
}

export interface Personne {
  id: string;
  nom: string;
  prenom: string;
  dateNaissance?: string;
  adresse?: string;
  telephone?: string;
  email?: string;
  nationalite?: string;
  profession?: string;
  notes?: string;
  enqueteId: string;
  dateCreation: string;
  dateModification: string;
}

export interface Lieu {
  id: string;
  nom: string;
  adresse: string;
  latitude?: number;
  longitude?: number;
  type: 'domicile' | 'travail' | 'autre';
  description?: string;
  enqueteId: string;
  dateCreation: string;
  dateModification: string;
}

export interface Photo {
  id: string;
  nom: string;
  url: string;
  taille: number;
  type: string;
  description?: string;
  enqueteId: string;
  dateCreation: string;
  dateModification: string;
}

export interface Fichier {
  id: string;
  nom: string;
  url: string;
  taille: number;
  type: string;
  description?: string;
  enqueteId: string;
  dateCreation: string;
  dateModification: string;
}

// Service générique pour les opérations CRUD
class CRUDService<T extends { id: string; dateCreation: string; dateModification: string }> {
  private storageKey: string;

  constructor(storageKey: string) {
    this.storageKey = storageKey;
  }

  // Récupérer tous les éléments
  async getAll(): Promise<T[]> {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error(`Erreur lors de la récupération des données ${this.storageKey}:`, error);
      return [];
    }
  }

  // Récupérer un élément par ID
  async getById(id: string): Promise<T | null> {
    try {
      const items = await this.getAll();
      return items.find(item => item.id === id) || null;
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'élément ${id}:`, error);
      return null;
    }
  }

  // Créer un nouvel élément
  async create(data: Omit<T, 'id' | 'dateCreation' | 'dateModification'>): Promise<T> {
    try {
      const items = await this.getAll();
      const newItem = {
        ...data,
        id: uuidv4(),
        dateCreation: new Date().toISOString(),
        dateModification: new Date().toISOString()
      } as T;

      items.push(newItem);
      localStorage.setItem(this.storageKey, JSON.stringify(items));
      return newItem;
    } catch (error) {
      console.error(`Erreur lors de la création de l'élément:`, error);
      throw error;
    }
  }

  // Mettre à jour un élément
  async update(id: string, data: Partial<Omit<T, 'id' | 'dateCreation'>>): Promise<T | null> {
    try {
      const items = await this.getAll();
      const index = items.findIndex(item => item.id === id);
      
      if (index === -1) {
        return null;
      }

      const updatedItem = {
        ...items[index],
        ...data,
        dateModification: new Date().toISOString()
      };

      items[index] = updatedItem;
      localStorage.setItem(this.storageKey, JSON.stringify(items));
      return updatedItem;
    } catch (error) {
      console.error(`Erreur lors de la mise à jour de l'élément ${id}:`, error);
      throw error;
    }
  }

  // Supprimer un élément
  async delete(id: string): Promise<boolean> {
    try {
      const items = await this.getAll();
      const filteredItems = items.filter(item => item.id !== id);
      
      if (filteredItems.length === items.length) {
        return false; // Élément non trouvé
      }

      localStorage.setItem(this.storageKey, JSON.stringify(filteredItems));
      return true;
    } catch (error) {
      console.error(`Erreur lors de la suppression de l'élément ${id}:`, error);
      throw error;
    }
  }

  // Récupérer les éléments par enquête
  async getByEnqueteId(enqueteId: string): Promise<T[]> {
    try {
      const items = await this.getAll();
      return items.filter(item => 'enqueteId' in item && (item as any).enqueteId === enqueteId);
    } catch (error) {
      console.error(`Erreur lors de la récupération des éléments pour l'enquête ${enqueteId}:`, error);
      return [];
    }
  }
}

// Instances des services
export const clientApi = new CRUDService<Client>('clients');
export const noteApi = new CRUDService<Note>('notes');
export const personneApi = new CRUDService<Personne>('personnes');
export const lieuApi = new CRUDService<Lieu>('lieux');
export const photoApi = new CRUDService<Photo>('photos');
export const fichierApi = new CRUDService<Fichier>('fichiers');

export default CRUDService;